/**
 * Ce que les familles disent, sur la page d'accueil publique.
 *
 * ## Pourquoi les avis sont choisis et non récupérés
 *
 * L'API Places de Google ne rend que cinq avis, « les plus pertinents » ou
 * « les plus récents », sans qu'on puisse dire lesquels — et ses conditions
 * interdisent de les stocker. Un avis tiède jugé pertinent atterrirait donc en
 * page d'accueil.
 *
 * Ils sont saisis depuis l'admin. Le lien « Voir tous les avis sur Google »
 * est ce qui rend la sélection honnête : le lecteur qui doute va vérifier.
 *
 * ## La section disparaît quand elle est vide
 *
 * Tant qu'aucun avis n'est saisi, rien ne s'affiche — pas un titre « Ils en
 * parlent » au-dessus de trois cartes grises. Une section vide fait plus de
 * mal qu'une section absente.
 */

import { ExternalLink, Quote, Star } from 'lucide-react';
import type { AvisGoogle } from '../../lib/reglages';

/** Cinq étoiles, pleines jusqu'à la note. */
const Etoiles = ({ note }: { note: number }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${note} sur 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={
          n <= Math.round(note)
            ? 'h-4 w-4 fill-amber-400 text-amber-400'
            : 'h-4 w-4 text-gray-300'
        }
        aria-hidden="true"
      />
    ))}
  </span>
);

/** « juillet 2026 » — le jour exact n'apporte rien à un témoignage. */
const moisEtAnnee = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-BE', { month: 'long', year: 'numeric' });
};

const AvisGoogleSection = ({ avis }: { avis: AvisGoogle }) => {
  if (avis.temoignages.length === 0) return null;

  return (
    <section className="bg-white py-12">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 text-center">
          <h2 className="mb-3 text-4xl text-gray-900">Ce que disent les familles</h2>

          {avis.note !== null && (
            <p className="flex flex-wrap items-center justify-center gap-2 text-lg text-gray-700">
              <Etoiles note={avis.note} />
              <span className="font-semibold">{avis.note.toLocaleString('fr-BE')} sur 5</span>
              {avis.nombre !== null && (
                <span className="text-gray-500">
                  · {avis.nombre} avis sur Google
                </span>
              )}
            </p>
          )}
        </div>

        <ul className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Trois avis au plus : la section ne doit pas repousser les stages trop bas */}
          {avis.temoignages.slice(0, 3).map((t, i) => (
            <li
              key={`${t.auteur}-${i}`}
              className="flex flex-col rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200"
            >
              <Quote className="h-6 w-6 flex-shrink-0 text-primary-200" aria-hidden="true" />
              <blockquote className="mt-3 flex-1 leading-relaxed text-gray-700">
                {t.texte}
              </blockquote>
              <footer className="mt-4 border-t border-gray-100 pt-3 text-sm">
                <span className="font-medium text-gray-900">{t.auteur}</span>
                {moisEtAnnee(t.date) && (
                  <span className="text-gray-500"> · {moisEtAnnee(t.date)}</span>
                )}
              </footer>
            </li>
          ))}
        </ul>

        <p className="mt-8 text-center">
          <a
            href={avis.lien}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-[44px] items-center font-medium text-primary-700 hover:text-primary-800"
          >
            Voir tous les avis sur Google
            <ExternalLink className="ml-1.5 h-4 w-4" aria-hidden="true" />
          </a>
        </p>
      </div>
    </section>
  );
};

export default AvisGoogleSection;
